import axios from "axios";
import { enqueue, listQueue, removeEntry } from "@/lib/offlineQueue";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// -------- Bearer --------
let bearer = null;
export function setBearer(token) {
  bearer = token;
}
api.interceptors.request.use((config) => {
  if (bearer) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${bearer}`;
  }
  return config;
});

// -------- Offline pub/sub --------
const offlineListeners = new Set();
export function subscribeOffline(fn) {
  offlineListeners.add(fn);
  return () => offlineListeners.delete(fn);
}
function emitOffline(evt) {
  offlineListeners.forEach((fn) => {
    try {
      fn(evt);
    } catch (err) {
      // A subscriber threw — don't let it break other subscribers
      console.error("[api] offline listener threw", err);
    }
  });
}

// -------- Mutation queueing --------
const MUTATION_METHODS = new Set(["POST", "PATCH", "PUT", "DELETE"]);

/**
 * A request is queueable if:
 *   - It's a mutation (POST/PATCH/PUT/DELETE)
 *   - It's targeted at a /api/* endpoint
 *   - It doesn't send a File / multipart (we can't reliably re-serialize File objects)
 *   - It's not explicitly opted-out with config.offlineQueue === false
 */
function isQueueable(config) {
  if (config.offlineQueue === false) return false;
  const m = (config.method || "get").toUpperCase();
  if (!MUTATION_METHODS.has(m)) return false;
  if (config.data instanceof FormData) return false;
  return true;
}

function isNetworkError(error) {
  if (!error) return false;
  if (error.code === "ERR_NETWORK") return true;
  if (error.message === "Network Error") return true;
  // If no response and we know we're offline
  if (!error.response && typeof navigator !== "undefined" && !navigator.onLine) return true;
  return false;
}

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const cfg = error.config || {};
    if (isNetworkError(error) && isQueueable(cfg)) {
      // Compute absolute URL relative to API base
      const url = cfg.url?.startsWith("http") ? cfg.url : `${API_BASE}${cfg.url}`;
      try {
        await enqueue({
          method: (cfg.method || "get").toUpperCase(),
          url,
          data: cfg.data ?? null,
          headers: {
            "Content-Type":
              cfg.headers?.["Content-Type"] ||
              cfg.headers?.["content-type"] ||
              "application/json",
          },
          entity_type: cfg.url,
        });
        emitOffline({ type: "queued" });
        emitOffline({ type: "offline" });
      } catch (e) {
        console.error("[offline] enqueue failed", e);
      }
      // Return a synthetic optimistic response so callers can proceed
      const fake = {
        data: { _offline: true, queued_at: new Date().toISOString() },
        status: 202,
        statusText: "Queued (offline)",
        headers: { "x-dkc-offline": "1" },
        config: cfg,
      };
      return fake;
    }
    return Promise.reject(error);
  },
);

// -------- Sync engine --------
let syncing = false;
export async function syncNow() {
  if (syncing) return { replayed: 0, failed: 0 };
  syncing = true;
  emitOffline({ type: "sync-start" });
  let replayed = 0;
  let failed = 0;
  try {
    const items = await listQueue();
    // Chronological order
    items.sort((a, b) => a.ts - b.ts);
    for (const item of items) {
      try {
        const body =
          item.data && typeof item.data === "string"
            ? item.data
            : item.data
              ? JSON.stringify(item.data)
              : undefined;
        const res = await fetch(item.url, {
          method: item.method,
          credentials: "include",
          headers: {
            ...(bearer ? { Authorization: `Bearer ${bearer}` } : {}),
            ...(body ? { "Content-Type": item.headers?.["Content-Type"] || "application/json" } : {}),
          },
          body,
        });
        if (res.ok || res.status === 404) {
          // 404 on DELETE is acceptable (already deleted / stale ref)
          await removeEntry(item.id);
          replayed += 1;
        } else if (res.status >= 400 && res.status < 500) {
          // Client error — drop to avoid infinite loop
          console.warn("[sync] client error, dropping entry", item, res.status);
          await removeEntry(item.id);
          failed += 1;
        } else {
          // 5xx: keep and retry later
          failed += 1;
          break;
        }
      } catch (e) {
        // Network still down — abort loop
        console.warn("[sync] network error, will retry", e);
        break;
      }
    }
  } finally {
    syncing = false;
    emitOffline({ type: "sync-end", replayed, failed });
  }
  return { replayed, failed };
}

// Auto-sync on reconnect
if (typeof window !== "undefined") {
  window.addEventListener("online", () => {
    emitOffline({ type: "online" });
    syncNow();
  });
  window.addEventListener("offline", () => {
    emitOffline({ type: "offline" });
  });
  // Kick a sync on startup, in case we crashed with a queue
  setTimeout(() => {
    if (navigator.onLine) syncNow();
  }, 1500);
}

// -------- Utilities --------
export default api;

export function formatApiErrorDetail(detail) {
  if (detail == null) return "Une erreur est survenue. Veuillez réessayer.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  return String(detail);
}
